import { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext.js';
import { Card, Row, Col, Button, Form, Modal, Image, InputGroup, Badge, Spinner } from 'react-bootstrap';
import Lottie from 'lottie-react';
import dotsAnimation from 'assets/lottie/dots.json';
import { useCollectionData, useCollection } from 'react-firebase-hooks/firestore';
import { db } from "config/firebase";
import { 
    query, collection, orderBy, startAt, limit, startAfter, endAt,
    getDocs, endBefore, addDoc, collectionGroup, where, getDoc, doc, updateDoc,
    getCountFromServer, writeBatch, deleteField
} from 'firebase/firestore';
import AdvanceTableWrapper from 'components/merlin/advance-table/AdvanceTableWrapper';
import AdvanceTable from 'components/merlin/advance-table/AdvanceTable';
import IconButton from 'components/common/IconButton';
import FalconCloseButton from 'components/common/FalconCloseButton';
import { toast } from 'react-toastify';

// Firestore 'in' queries limited to 30 items
const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
        chunks.push(array.slice(i, i + size));
    }
    return chunks;
};

// ============================================================
// BATCH FETCH HELPER FUNCTIONS (Phase 2A Optimization)
// These functions batch multiple queries into parallel operations
// to eliminate N+1 query patterns in sendBatchToPick()
// ============================================================

// Batch fetch variants by SKU using collectionGroup
async function batchFetchVariantsBySku(skus) {
    if (skus.length === 0) return new Map();

    const chunks = chunkArray(skus, 30);
    const queryPromises = chunks.map(chunk =>
        getDocs(query(
            collectionGroup(db, "variants"),
            where("sku", "in", chunk)
        ))
    );

    const snapshots = await Promise.all(queryPromises);

    // Map<sku, variant[]> - one SKU can have multiple variants
    const variantsMap = new Map();
    snapshots.forEach(snapshot => {
        snapshot.forEach(doc => {
            const data = doc.data();
            data.id = doc.id;
            data.ref = doc.ref;
            const existing = variantsMap.get(data.sku) || [];
            existing.push(data);
            variantsMap.set(data.sku, existing);
        });
    });

    return variantsMap;
}

// Batch fetch parent products by ref
async function batchFetchParentProducts(parentRefs) {
    if (parentRefs.length === 0) return new Map();

    // Filter out null/undefined refs, then deduplicate by path
    const validRefs = parentRefs.filter(ref => ref && ref.path);
    if (validRefs.length === 0) return new Map();

    const uniqueRefs = [...new Map(validRefs.map(ref => [ref.path, ref])).values()];

    // Parallel getDoc for all unique parents
    const parentPromises = uniqueRefs.map(ref => getDoc(ref));
    const parentDocs = await Promise.all(parentPromises);

    // Map<refPath, parentData>
    const parentsMap = new Map();
    parentDocs.forEach(doc => {
        if (doc.exists()) {
            const data = doc.data();
            data.id = doc.id;
            data.ref = doc.ref;
            parentsMap.set(doc.ref.path, data);
        }
    });

    return parentsMap;
}

// Batch fetch location data for 3PL products
async function batchFetchLocationData(skuLocationPairs) {
    if (skuLocationPairs.length === 0) return new Map();

    // Group by locationId to minimize queries
    const byLocation = new Map();
    skuLocationPairs.forEach(({ sku, locationId }) => {
        const skus = byLocation.get(locationId) || [];
        skus.push(sku);
        byLocation.set(locationId, skus);
    });

    // Query each location with batched SKUs
    const queryPromises = [];
    for (const [locationId, skus] of byLocation) {
        const chunks = chunkArray(skus, 30);
        chunks.forEach(chunk => {
            queryPromises.push(
                getDocs(query(
                    collection(db, "locationCountData"),
                    where("sku", "in", chunk),
                    where("locationId", "==", locationId)
                ))
            );
        });
    }

    const snapshots = await Promise.all(queryPromises);

    // Map<"sku:locationId", locationData>
    const locationMap = new Map();
    snapshots.forEach(snapshot => {
        snapshot.forEach(doc => {
            const data = doc.data();
            data.id = doc.id;
            data.ref = doc.ref;
            const key = `${data.sku}:${data.locationId}`;
            locationMap.set(key, data);
        });
    });

    return locationMap;
}

// ============================================================

function BatchesTable({ batches }) {

    const baseUrl = 'localhost:3000'

    const [enhancedBatches, setEnhancedBatches] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedBatchForAssignment, setSelectedBatchForAssignment] = useState(null);
    const [sendBatchToPickExecuting, setSendBatchToPickExecuting] = useState(false);
    const [deleteExec, setDeleteExec] = useState(false);

    async function deleteBatch(batch) {
        try {
            setDeleteExec(true);

            // Update batch status
            await updateDoc(batch.ref, {
                status: 'DELETED',
                "timestamp.modified": new Date(),
            });

            const orders = batch.orders;
            if (!orders || orders.length === 0) {
                setDeleteExec(false);
                toast.success('Batch deleted.', { theme: 'colored' });
                return;
            }

            // Collect order IDs (handle both string and object formats)
            const orderIds = orders
                .map(o => typeof o === 'string' ? o : o?.orderId)
                .filter(Boolean);

            if (orderIds.length === 0) {
                setDeleteExec(false);
                toast.success('Batch deleted.', { theme: 'colored' });
                return;
            }

            // Batch query all orders (chunked for Firestore 'in' limit)
            const chunks = chunkArray(orderIds, 30);
            const queryPromises = chunks.map(chunk =>
                getDocs(query(collection(db, "orders"), where("orderId", "in", chunk)))
            );
            const snapshots = await Promise.all(queryPromises);

            // Batch update all orders
            const batchWrite = writeBatch(db);
            snapshots.forEach(snapshot => {
                snapshot.forEach(doc => {
                    batchWrite.update(doc.ref, {
                        assignedToBatch: deleteField(),
                        batchId: deleteField(),
                        batchName: deleteField(),
                    });
                });
            });

            await batchWrite.commit();
            toast.success(`Batch deleted. ${orderIds.length} orders returned to pool.`, { theme: 'colored' });
            setDeleteExec(false);
        } catch (error) {
            setDeleteExec(false);
            console.error(error);
            toast.error('Error deleting batch', { theme: 'colored' });
        }
    }

    // Fetch all orders for all batches in batched queries (avoids N+1)
    async function fetchAllOrdersForBatches(batchList) {
        // Performance instrumentation
        const perfStart = performance.now();

        // 1. Collect all unique order IDs from all batches
        const allOrderIds = new Set();
        batchList.forEach(batch => {
            batch.orders?.forEach(order => {
                // Handle both string IDs and object format
                const orderId = typeof order === 'string' ? order : order?.orderId;
                if (orderId) allOrderIds.add(orderId);
            });
        });

        if (allOrderIds.size === 0) {
            console.log('[PERF] fetchAllOrdersForBatches: 0 orders to fetch');
            return new Map();
        }

        // 2. Chunk into groups of 30 (Firestore 'in' limit)
        const orderIdChunks = chunkArray([...allOrderIds], 30);

        console.log(`[PERF] fetchAllOrdersForBatches: ${allOrderIds.size} orders in ${orderIdChunks.length} batched queries`);

        // 3. Execute all chunk queries in parallel
        const queryStart = performance.now();
        const queryPromises = orderIdChunks.map(chunk =>
            getDocs(query(
                collection(db, "orders"),
                where("orderId", "in", chunk)
            ))
        );

        const snapshots = await Promise.all(queryPromises);
        const queryEnd = performance.now();

        // 4. Build Map<orderId, orderData> for O(1) lookups
        const ordersMap = new Map();
        snapshots.forEach(snapshot => {
            snapshot.forEach(doc => {
                const data = doc.data();
                data.id = doc.id;
                data.ref = doc.ref;
                ordersMap.set(data.orderId, data);
            });
        });

        const perfEnd = performance.now();
        console.log(`[PERF] fetchAllOrdersForBatches complete:
  - Orders fetched: ${ordersMap.size}
  - Firestore queries: ${orderIdChunks.length}
  - Query time: ${(queryEnd - queryStart).toFixed(2)}ms
  - Total time: ${(perfEnd - perfStart).toFixed(2)}ms`);

        return ordersMap;
    }

    // Enhance a single batch using pre-fetched orders map
    function enhanceBatchWithOrders(batch, ordersMap) {
        // Get orders for this batch from the map
        const batchOrders = [];
        batch.orders?.forEach(order => {
            const orderId = typeof order === 'string' ? order : order?.orderId;
            if (orderId && ordersMap.has(orderId)) {
                batchOrders.push(ordersMap.get(orderId));
            }
        });

        // Collect all items from orders
        const items = [];
        batchOrders.forEach(order => {
            order.items?.forEach(item => items.push(item));
        });

        // Count unique SKUs
        const skuCounts = new Map();
        items.forEach(item => {
            skuCounts.set(item.sku, (skuCounts.get(item.sku) || 0) + 1);
        });

        const uniqueSkus = [...skuCounts.keys()];
        const totalItemsCount = [...skuCounts.entries()].map(([sku, count]) => ({
            sku,
            count
        }));

        return {
            ...batch,
            orders: batchOrders,
            skus: uniqueSkus,
            totalItemsCount
        };
    }

    useEffect(() => {
        if (!batches || batches.length === 0) {
            setEnhancedBatches([]);
            return;
        }

        let isSubscribed = true;

        async function enhanceAllBatches() {
            const totalStart = performance.now();
            console.log(`[PERF] ========== BATCH ENHANCEMENT START ==========`);
            console.log(`[PERF] Batches to enhance: ${batches.length}`);

            try {
                // Fetch all orders for all batches in one batched operation
                const fetchStart = performance.now();
                const ordersMap = await fetchAllOrdersForBatches(batches);
                const fetchEnd = performance.now();

                // Enhance all batches using the pre-fetched data
                const enhanceStart = performance.now();
                const enhanced = batches.map(batch =>
                    enhanceBatchWithOrders(batch, ordersMap)
                );
                const enhanceEnd = performance.now();

                // Single state update
                if (isSubscribed) {
                    setEnhancedBatches(enhanced);
                }

                const totalEnd = performance.now();
                const totalOrders = enhanced.reduce((sum, b) => sum + (b.orders?.length || 0), 0);
                const totalItems = enhanced.reduce((sum, b) => {
                    return sum + b.orders?.reduce((oSum, o) => oSum + (o.items?.length || 0), 0) || 0;
                }, 0);

                console.log(`[PERF] ========== BATCH ENHANCEMENT COMPLETE ==========
  📊 Summary:
  - Batches: ${batches.length}
  - Total orders: ${totalOrders}
  - Total items: ${totalItems}

  ⏱️ Timing:
  - Fetch orders: ${(fetchEnd - fetchStart).toFixed(2)}ms
  - Enhance batches: ${(enhanceEnd - enhanceStart).toFixed(2)}ms
  - TOTAL TIME: ${(totalEnd - totalStart).toFixed(2)}ms

  🚀 Compared to N+1 (estimated):
  - Old: ~${totalOrders} sequential queries
  - New: ~${Math.ceil(totalOrders / 30)} parallel queries
  - Estimated savings: ${totalOrders > 0 ? Math.round((1 - Math.ceil(totalOrders / 30) / totalOrders) * 100) : 0}% fewer queries
==========================================================`);

            } catch (error) {
                console.error('Error enhancing batches:', error);
                if (isSubscribed) {
                    setEnhancedBatches([]);
                }
            }
        }

        enhanceAllBatches();

        return () => {
            isSubscribed = false;
        };
    }, [batches])

    const ReassignUser = ({ batch }) => {
        const assignedUser = batch.assignedUser || 'None';
        const [users, setUsers] = useState([]);
        const [newAssignedUser, setNewAssignedUser] = useState('');
        const [assignmentExecuting, setAssignmentExecuting] = useState(false);
        const handleClose = () => setShowAssignModal(false);

        async function gatherAssignableUsers() {
            const users = [];
            const querySnapshot = await getDocs(query(collection(db, "users"), where("fulfillmentAssignable", "==", true)));
            querySnapshot.forEach((doc) => {
                users.push(doc.data());
            });
            // remove the current assignedUser from the list of options
            const index = users.findIndex(user => user.name === assignedUser);
            if (index !== -1) {
                users.splice(index, 1);
            }
            return users;
        }

        useEffect(() => {
            if (!batch) return;
            let isSubscribed = true;
            gatherAssignableUsers().then((users) => {
                if (isSubscribed) setUsers(users);
            });
            return () => isSubscribed = false;
        }, [batch]);

        async function handleReassignment(newAssignedUser) {
            try {
                setAssignmentExecuting(true);
                await updateDoc(batch.ref, {
                    assignedUser: newAssignedUser
                });
                toast.success(`Batch reassigned to ${newAssignedUser}`, { theme: 'colored' });
                setAssignmentExecuting(false);
                handleClose();
            } catch (error) {
                console.log(error);
                toast.error('Error reassigning batch to user', { theme: 'colored' });
            }
        }

        const UserNameDropDown = ({ users }) => {
            return (
                <>
                    <Form.Group className="mb-3" controlId="formBasicUserSelect">
                    <Form.Label>Assign To User (Optional)</Form.Label>
                    <Form.Select 
                        aria-label="Select User" 
                        onChange={(e) => setNewAssignedUser(e.target.value)}
                    >
                        <option>Select User</option>
                        {users.map((user, index) => (
                            <option key={`${index}-${user.id}`} value={user.id}>{user.name}</option>
                        ))}
                    </Form.Select>
                    </Form.Group>
                    { newAssignedUser !== '' &&
                        <div className='mt-3'>
                            <Button
                                variant="success"
                                onClick={() => handleReassignment(newAssignedUser)}
                                disabled={assignmentExecuting}
                            >Assign Batch to {newAssignedUser}</Button>
                        </div>
                    }
                </>
            )
        }

        return (
            <> 
                {assignedUser === 'None' ?
                    <div>
                        <h6>Whoa this batch has no assigned user!</h6>
                        <p>Select user below that the batch will be assigned to:</p>
                    </div>
                :   <div>
                        <h6>This batch is currently assigned to {assignedUser}</h6>
                        <p>Select user below that the batch will be reassigned to:</p>
                    </div>
                }
                <UserNameDropDown users={users} />
            </>
            
        )
    }

    function handleAssignmentClick(e, batch) {
        e.preventDefault();
        setShowAssignModal(true);
        setSelectedBatchForAssignment(batch);
    }

    const colDef = [
        { 
            accessor: 'batchName',
            Header: 'Batch Name',
            Cell: ({ row }) => {
                const { batchName, id } = row.original;
                return (
                    <a href={`/fulfillment/batches/${id}`}>{batchName}</a>
                )
            }
        },
        {
            Header: 'Created',
            accessor: 'timestamp.created.seconds',
        },
        {
            Header: 'Assigned User',
            Cell: ({ row }) => {
                const { assignedUser } = row.original;
                if (!assignedUser) {
                    return (
                        <Button
                            size='sm'
                            variant='outline-primary'
                            onClick={(e) => handleAssignmentClick(e, row.original)}
                            disabled={assignedUser}
                        >Assign User</Button>
                    )
                }
                return (
                    <span>{assignedUser}</span>
                )
            }
        },
        {
            Header: 'Orders in Batch',
            Cell: ({ row }) => {
                const { orders } = row.original;
                // reduce the count in totalItemsCount to get an aggregate number of items in batch
                const totalOrders = orders?.length;
                return (
                    <>
                        <div className='block text-700 fw-bold'>{totalOrders}</div>
                    </>   
                )
            }
        },
        {
            Header: 'Actions',
            Cell: ({ row }) => {
                // reduce the count in totalItemsCount to get an aggregate number of items in batch
                return (
                    <>
                        <IconButton 
                            size='sm'
                            variant='outline-default'
                            icon='user'
                            className='me-2'
                            onClick={() => sendBatchToPick(row.original)}
                            disabled={sendBatchToPickExecuting}
                        >
                            {!sendBatchToPickExecuting ? 'Send To Pick' : <Spinner animation="border" variant="primary" /> }
                        </IconButton>

                        <IconButton
                            variant="outline-default"
                            size="sm"
                            icon="trash-alt"
                            transform="shrink-3"
                            className='me-2 text-danger'
                            onClick={() => deleteBatch(row.original)}
                            disabled={deleteExec}
                        >
                            <span className="d-none d-sm-inline-block ms-1">Delete</span>
                        </IconButton>
                    </>   
                )
            }
        },
    ];

    console.log(enhancedBatches[0])

    function sendMultipleBatchesForPick(batches) {
        setSendBatchToPickExecuting(true);
        try {
            if (!batches) return;
            batches.forEach((batch) => {
                sendBatchToPick(batch, false);
            })
            toast.success(`${batches.length} Batches sent to pick`, { theme: 'colored' });
            setSendBatchToPickExecuting(false);
        } catch (error) {
            console.log(error);
            toast.error('Error sending batches to pick', { theme: 'colored' });
            setSendBatchToPickExecuting(false);
        }
    }

    const SendMultiBatchToPick = ({ batches }) => {
        return (
            <>
                <div className='block text-700 fw-bold'>Send {batches.length} Batches to Pick</div>
                <div className='mt-3'>
                    <Button
                        variant="success"
                        onClick={() => sendMultipleBatchesForPick(batches)}
                    >Send Batches to Pick</Button>
                </div>
            </>
        )
    }

    function BulAction(table){
        
        const { selectedRowIds, page } = table;
        const [showBulkModal, setShowBulkModal] = useState(false);
        const [bulkActionOption, setBulkActionOption] = useState('');
        const handleClose = () => setShowBulkModal(false);
        let selectedRows = [];
        if (selectedRowIds && page) {
            selectedRows = Object.keys(selectedRowIds).map(id => page.find(row => row.id === id)?.original);
        }

        return (
            <>
                <Row className="flex mb-3">
                <Col xs={4} sm="auto" className="d-flex align-items-center pe-0">
                    <h5 className="fs-0 mb-0 text-nowrap py-2 py-xl-0">
                    {
                        Object.keys(selectedRowIds).length > 0 ?
                        'You have selected ' + Object.keys(selectedRowIds).length + ' batches' 
                        :
                        null
                    }
                    </h5>
                </Col>
                <Col xs={8} sm="auto" className="ms-auto text-end ps-0">
                    {Object.keys(selectedRowIds).length > 0 ? (
                    <div className="d-flex">
                        <Form.Select size="sm" aria-label="Bulk actions" onChange={(e) => setBulkActionOption(e.target.value)}>
                        <option>Bulk Actions</option>
                        <option value="sendToPick">Send To Pick</option>
                        </Form.Select>
                        <Button
                        type="button"
                        variant="falcon-default"
                        size="sm"
                        className="ms-2"
                        onClick={() => setShowBulkModal(true)}
                        >
                        Apply
                        </Button>
                    </div>
                    ) : (
                        <div id="orders-actions">
                        <IconButton
                            variant="falcon-default"
                            size="sm"
                            icon="plus"
                            transform="shrink-3"
                            className='me-2'
                            onClick={() => setModalShow(true)}
                        >
                            <span className="d-none d-sm-inline-block ms-1">New Batch</span>
                        </IconButton>
                        </div>
                    )}
                </Col>
                </Row>
                <Modal show={showBulkModal} onHide={handleClose} backdrop="static" keyboard={false}>
                    <Modal.Header>
                        <Modal.Title>
                            {bulkActionOption === 'sendToPick' && 'Send Batches for Picking'}
                        </Modal.Title>
                        <FalconCloseButton onClick={handleClose}/>
                    </Modal.Header>
                    <Modal.Body>
                        {bulkActionOption === 'sendToPick' && <SendMultiBatchToPick batches={selectedRows}/>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                        Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        );
    };

    function AssignUserModal({ show, setShow, batch }) {
    
      const handleClose = () => setShow(false);
    
      return (
        <>
          <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
            <Modal.Header>
              <Modal.Title>Assign User</Modal.Title>
              <FalconCloseButton onClick={handleClose}/>
            </Modal.Header>
            <Modal.Body>
                <ReassignUser batch={batch} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      );
    }

    async function sendBatchToPick(batch, showToast = true) {
        // this function updates the batch status to IN_PICK
        // and sends an outgoingMail to the user assigned to the batch
        // with a link to the batch's pick wizard
        console.log(batch)
        setSendBatchToPickExecuting(true);
        try {
            await updateDoc(batch.ref, {
                status: 'IN_PICK',
                "timestamp.modified": new Date(),
            });
            const assignedUser = batch.assignedUser;
            const userQuery = query(
                collection(db, "users"),
                where("name", "==", assignedUser),
            );
            const userQuerySnapshot = await getDocs(userQuery);
            let usersRaw = [];
            userQuerySnapshot.forEach((doc) => {
                let temp = doc.data();
                temp.id = doc.id;
                temp.ref = doc.ref;
                usersRaw.push(temp);
            });
            const user = usersRaw[0];

            const orders = batch?.orders;

            // get the items in each order
            const itemsInOrders = [];
            for (let i = 0; i < orders?.length; i++) {
                const order = orders[i];
                const items = order.items;
                for (let j = 0; j < items?.length; j++) {
                    const item = items[j];
                    itemsInOrders.push({ 
                        sku: item.sku,
                        quantity: item.quantity,
                    });
                }
            }

            // combine like items together by increasing the quantity
            const items = [];
            itemsInOrders.forEach((item) => {
                const index = items.findIndex(i => i.sku === item.sku);
                if (index === -1) {
                    items.push(item);
                } else {
                    items[index].quantity += item.quantity;
                }
            });

            // ============================================================
            // BATCHED ITEM ENRICHMENT (Phase 2A Optimization)
            // Instead of N+1 sequential queries, we batch-fetch all data
            // and then assemble items using O(1) Map lookups
            // ============================================================
            const perfStart = performance.now();
            console.log(`[PERF] ========== SEND BATCH TO PICK - ITEM ENRICHMENT START ==========`);

            // Collect unique SKUs from items
            const uniqueSkus = [...new Set(items.map(item => item.sku))];
            console.log(`[PERF] Unique SKUs to process: ${uniqueSkus.length}`);

            // Phase 1: Batch fetch all variants
            const variantFetchStart = performance.now();
            const variantsMap = await batchFetchVariantsBySku(uniqueSkus);
            console.log(`[PERF] Phase 1 - Variants fetched: ${variantsMap.size} (${(performance.now() - variantFetchStart).toFixed(2)}ms)`);

            // Phase 2: Collect and batch fetch all parent products
            const parentFetchStart = performance.now();
            const allParentRefs = [];
            const bundleVariants = []; // Track bundles for later processing
            variantsMap.forEach((variants, sku) => {
                variants.forEach(v => {
                    if (v.parentRef) allParentRefs.push(v.parentRef);
                    if (v.isBundle) bundleVariants.push({ sku, variant: v });
                });
            });

            const parentsMap = await batchFetchParentProducts(allParentRefs);
            console.log(`[PERF] Phase 2 - Parents fetched: ${parentsMap.size} (${(performance.now() - parentFetchStart).toFixed(2)}ms)`);

            // Phase 3: Identify 3PL products and batch fetch location data
            const locationFetchStart = performance.now();
            const skuLocationPairs = [];
            variantsMap.forEach((variants, sku) => {
                variants.forEach(v => {
                    if (v.preferredFullfillmentLocation && v.preferredFullfillmentLocation !== '0') {
                        skuLocationPairs.push({
                            sku: v.sku,
                            locationId: v.preferredFullfillmentLocation
                        });
                    }
                });
            });

            const locationMap = await batchFetchLocationData(skuLocationPairs);
            console.log(`[PERF] Phase 3 - Locations fetched: ${locationMap.size} (${(performance.now() - locationFetchStart).toFixed(2)}ms)`);

            // Phase 4: Handle bundles - fetch bundle component parents
            const bundleFetchStart = performance.now();
            const bundleComponentRefs = [];
            bundleVariants.forEach(({ variant }) => {
                const parentData = parentsMap.get(variant.parentRef?.path);
                if (parentData?.includedVariants) {
                    parentData.includedVariants.forEach(comp => {
                        if (comp.parentRef) bundleComponentRefs.push(comp.parentRef);
                    });
                }
            });

            if (bundleComponentRefs.length > 0) {
                const componentParentsMap = await batchFetchParentProducts(bundleComponentRefs);
                // Merge into parentsMap
                componentParentsMap.forEach((v, k) => parentsMap.set(k, v));
                console.log(`[PERF] Phase 4 - Bundle components fetched: ${componentParentsMap.size} (${(performance.now() - bundleFetchStart).toFixed(2)}ms)`);
            }

            // Phase 5: Build enriched items using the pre-fetched data
            const buildStart = performance.now();
            const itemsWithDetails = [];
            const itemsWithoutDetails = [];

            for (const item of items) {
                const variants = variantsMap.get(item.sku);

                if (!variants || variants.length === 0) {
                    itemsWithoutDetails.push(item);
                    continue;
                }

                for (const variant of variants) {
                    if (!variant.isBundle) {
                        const parentData = parentsMap.get(variant.parentRef?.path);
                        if (!parentData) continue;

                        const fulfillmentLocation = variant.preferredFullfillmentLocation;
                        let enhancedItem;

                        if (fulfillmentLocation === '0') {
                            // Topicz product - use IBMi data from parent
                            enhancedItem = {
                                orderedSku: variant.sku,
                                parentSku: parentData.sku,
                                name: parentData.name,
                                slot: parentData.ibmi?.slot || 'None',
                                ibmiOnhand: parentData.ibmi?.onhand || 0,
                                subItem: parentData.ibmi?.subItem || 'None',
                                image: parentData?.primaryImage || parentData?.files?.[0] || 'None',
                            };
                        } else {
                            // 3PL product - use pre-fetched location data
                            const locationKey = `${variant.sku}:${fulfillmentLocation}`;
                            const slotData = locationMap.get(locationKey);
                            // IMPORTANT: slotData must be undefined (not {}) when not found
                            // so PickDetail.js correctly skips inventory updates via `if (item?.slotData)`

                            enhancedItem = {
                                orderedSku: variant.sku,
                                parentSku: parentData.sku,
                                name: parentData.name,
                                slot: slotData?.slot || "Unknown",
                                ibmiOnhand: slotData?.count || "0",
                                subItem: parentData?.ibmi?.subItem || 'None',
                                image: parentData?.primaryImage || parentData?.files?.[0] || 'None',
                                slotData: slotData,
                            };
                        }

                        // Apply product factor for quantity calculation
                        // Validate productFactor: must be positive finite number, default to 1
                        // Also handles string numbers (e.g., "2") via parseFloat
                        const rawPf = typeof variant.productFactor === 'number'
                            ? variant.productFactor
                            : parseFloat(variant.productFactor);
                        const pf = (typeof rawPf === 'number' &&
                                    rawPf > 0 &&
                                    isFinite(rawPf))
                                   ? rawPf : 1;

                        if (pf < 1) {
                            // Break-box: ceiling of quantity / productFactor
                            const newQuantity = Math.ceil(item.quantity / pf);
                            enhancedItem.quantity = Number(newQuantity);
                        } else {
                            // Standard: quantity * productFactor
                            const newQuantity = item.quantity * pf;
                            enhancedItem.quantity = Number(newQuantity);
                        }

                        itemsWithDetails.push(enhancedItem);
                    } else {
                        // Bundle - expand using pre-fetched bundle data
                        const bundleData = parentsMap.get(variant.parentRef?.path);
                        if (!bundleData?.includedVariants) continue;

                        for (const component of bundleData.includedVariants) {
                            const componentParent = parentsMap.get(component.parentRef?.path);
                            if (!componentParent) continue;

                            let enhancedItem = {
                                orderedSku: component.sku,
                                parentSku: componentParent.sku,
                                slot: componentParent.ibmi?.slot || 'None',
                                name: componentParent.name,
                                ibmiOnhand: componentParent.ibmi?.onhand || 0,
                                subItem: componentParent.ibmi?.subItem || 'None',
                                bundleComponent: true,
                                image: componentParent?.primaryImage || componentParent?.files?.[0] || 'None',
                            };

                            // Apply product factor for bundle component quantity
                            // Validate productFactor: must be positive finite number, default to 1
                            // Also handles string numbers (e.g., "2") via parseFloat
                            const rawCompPf = typeof component.productFactor === 'number'
                                ? component.productFactor
                                : parseFloat(component.productFactor);
                            const compPf = (typeof rawCompPf === 'number' &&
                                            rawCompPf > 0 &&
                                            isFinite(rawCompPf))
                                           ? rawCompPf : 1;

                            if (compPf < 1) {
                                enhancedItem.quantity = Math.ceil(item.quantity / compPf);
                            } else {
                                enhancedItem.quantity = item.quantity * compPf;
                            }

                            itemsWithDetails.push(enhancedItem);
                        }
                    }
                }
            }

            const perfEnd = performance.now();
            console.log(`[PERF] Phase 5 - Items built: ${itemsWithDetails.length} (${(performance.now() - buildStart).toFixed(2)}ms)`);
            console.log(`[PERF] ========== SEND BATCH TO PICK - ITEM ENRICHMENT COMPLETE ==========
  - Unique SKUs: ${uniqueSkus.length}
  - Variants fetched: ${variantsMap.size}
  - Parents fetched: ${parentsMap.size}
  - 3PL locations fetched: ${locationMap.size}
  - Items without details: ${itemsWithoutDetails.length}
  - Total items enriched: ${itemsWithDetails.length}
  - TOTAL TIME: ${(perfEnd - perfStart).toFixed(2)}ms
==========================================================`);
            // ============================================================
            // sort itemsWithDetails by slot
            itemsWithDetails.sort((a, b) => (a.slot > b.slot) ? 1 : -1);

            // check for items that are out of stock
            const outOfStockItems = [];
            itemsWithDetails.forEach((item) => {
                if (item.quantity > item.ibmiOnhand) {
                    outOfStockItems.push(item);
                }
            });

            // check for items which are nearly out of stock
            const nearlyOutOfStockItems = [];
            itemsWithDetails.forEach((item) => {
                if (item.quantity > (item.ibmiOnhand / 2)) {
                    nearlyOutOfStockItems.push(item);
                }
            });

            // check for items which are low in stock
            const lowStockItems = [];
            itemsWithDetails.forEach((item) => {
                if (item.quantity > (item.ibmiOnhand / 4)) {
                    lowStockItems.push(item);
                }
            });

            const pickWizardJob = {
                batchId: batch.id,
                batchName: batch.batchName,
                assignedUser: batch.assignedUser,
                assignedUserUid: user.uid,
                assignedUserEmail: user.email || 'None',
                assignedUserPhone: user.phone || 'None',
                items: itemsWithDetails,
                outOfStockItems: outOfStockItems,
                nearlyOutOfStockItems: nearlyOutOfStockItems,
                lowStockItems: lowStockItems,
                timestamp: {
                    created: new Date(),
                    modified: new Date(),
                },
                status: "NOT STARTED",
            }


            // create a new pickWizardJob
            const pickWizardJobRef = await addDoc(collection(db, "pickWizardJobs"), pickWizardJob);
            const pickWizardJobId = pickWizardJobRef.id;
            const linkToPickWizard = `https://${baseUrl}/pick/${pickWizardJobId}`;

            const mail = {
                to: user.email,
                subject: 'Batch Ready To Pick',
                message: `Your batch ${batch.batchName} is ready to pick. Click the link below to begin picking:`,
                link: `${linkToPickWizard}`
            }
            await addDoc(collection(db, "outgoingMail"), {
                to: ['greg@meetmarty.com'],
                message: {
                    subject: mail.subject,
                    text: `${mail.message} \n\n\n ${mail.link}`,
                }
            });

            if (showToast) toast.success('Batch sent to pick', { theme: 'colored' });
            // only turn this off if showToast is true; showToast is set to true when it is a single batch being sent to pick
            // otherwise there are multiple batches being sent to pick which will have an inaccurate loading state if you
            // turn this false before they have a chance to finish
            if (showToast) setSendBatchToPickExecuting(false);
        } catch (error) {
            console.log(error);
            toast.error('Error sending batch to pick', { theme: 'colored' });
        }
    }

    return (
        enhancedBatches?.length > 0 
        ?
            <>
            <AdvanceTableWrapper
                columns={colDef}
                data={enhancedBatches}
                sortable
                pagination
                perPage={50}
                selection
                selectionColumnWidth={30}
            >
                <div className="mt-3">
                    <BulAction table/>
                </div>
                
                <AdvanceTable
                    table
                    headerClassName="bg-200 text-900 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap"
                    tableProps={{
                    striped: true,
                    className: 'fs--1 mb-0 overflow-hidden'
                    }}
                />
            </AdvanceTableWrapper>
            <AssignUserModal show={showAssignModal} setShow={setShowAssignModal} batch={selectedBatchForAssignment}/>
            </>
        :
        <div className="text-center">
            <h3>No Orders</h3>
        </div>
    )
}

const OpenBatches = ({ children }) => {

    // todo need to do batch data enhancement here instead of in the table component
    const { user, authLoading }= useContext(FirebaseAuthContext)
    const navigate = useNavigate();
    const [batchesRaw, batchesLoading, batchesError] = useCollection(
        query(
            collection(db, "customBatches"),
            where("status", "!=", "DELETED"),
            // orderBy("timestamp.created", "desc"),
        )
    );

    const [batches, setBatches] = useState([]);
    useEffect(() => {
        if (batchesLoading) return;
        if (batchesError) {
            console.log(batchesError);
            return;
        }
        if (!batchesRaw) return;
        const dataForState = [];
        batchesRaw.forEach((doc) => {
            let temp = doc.data();
            temp.id = doc.id;
            temp.ref = doc.ref;
            dataForState.push(temp)
        });
        setBatches(dataForState);
    }, [batchesRaw])

    // Redirect to home if not logged in
    useEffect(() => {   
        if (authLoading) return;
        if (!user) return navigate('/');
      }, [authLoading, user])

    return (
        !batchesLoading ?
        <>
            <Card>
                <Card.Body>
                    <BatchesTable 
                        batches={batches}
                        className="mt-2" 
                    />
                </Card.Body>
            </Card>
        </>
        :
        <Lottie animationData={dotsAnimation} style={{width: 100, height: 100}} />
    );
}

export default OpenBatches;
